import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  telegramId: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  photoUrl?: string;
  balance: number;
  totalEarned: number;
  level: number;
  experience: number;
  streak: number;
  referralCode: string;
  walletAddress?: string;
  kycVerified: boolean;
  rank?: number;
  isBanned?: boolean;
  banReason?: string;
}

interface AutoFaucetStatus {
  isActive: boolean;
  energy: number;
  maxEnergy: number;
  sessionToken?: string;
}

interface AppState {
  // Auth
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Auto faucet
  autoFaucet: AutoFaucetStatus;

  // Actions
  setToken: (token: string | null) => void;
  setUser: (user: User | null) => void;
  updateBalance: (balance: number) => void;
  updateLevel: (level: number, experience: number) => void;
  setAutoFaucet: (status: Partial<AutoFaucetStatus>) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      // Initial state
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: true,
      autoFaucet: {
        isActive: false,
        energy: 0,
        maxEnergy: 100,
      },

      // Actions
      setToken: (token) => set({ token, isAuthenticated: !!token }),

      setUser: (user) => set({ user, isAuthenticated: !!user }),

      updateBalance: (balance) =>
        set((state) => ({
          user: state.user ? { ...state.user, balance } : null,
        })),

      updateLevel: (level, experience) =>
        set((state) => ({
          user: state.user ? { ...state.user, level, experience } : null,
        })),

      setAutoFaucet: (status) =>
        set((state) => ({
          autoFaucet: { ...state.autoFaucet, ...status },
        })),

      setLoading: (isLoading) => set({ isLoading }),

      logout: () =>
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          autoFaucet: { isActive: false, energy: 0, maxEnergy: 100 },
        }),
    }),
    {
      name: 'gemifaucet-storage',
      partialize: (state) => ({
        token: state.token,
        // Only persist user identity, NOT balance - balance always comes from server
        // Balance fields are explicitly set to 0 to avoid caching
        user: state.user ? {
          telegramId: state.user.telegramId,
          username: state.user.username,
          firstName: state.user.firstName,
          lastName: state.user.lastName,
          photoUrl: state.user.photoUrl,
          referralCode: state.user.referralCode,
          walletAddress: state.user.walletAddress,
          level: state.user.level,
          experience: state.user.experience,
          streak: state.user.streak,
          kycVerified: state.user.kycVerified,
          rank: state.user.rank,
          // Ban status - always fetch from server but persist for UI
          isBanned: state.user.isBanned,
          banReason: state.user.banReason,
          // DO NOT cache balance - always fetch from server
          balance: 0,
          totalEarned: 0,
        } : null,
      }),
    }
  )
);
